/**
 * Which zone should the app context into in order to work on a given job?
 *
 * A job has an *owning* zone (`job.zone`, the `IN_ZONE` relationship) and zero
 * or more *shared* zones (`job.sharedZones`, the `SHARED_WITH_ZONE` relationship).
 * Interstate jobs are owned by an `area` zone under `ymm-interstate-usa` and
 * shared with the franchise that actually works them, so for the franchise user
 * the owning zone is unreachable — they hold no role anywhere in its ancestry.
 *
 * Asking the backend to context into an unreachable zone does not error. It
 * silently substitutes the user's alphabetically-first contextable zone
 * (`resolveFirstContextableZone` in `fa.authenticate.ts`, which is
 * `ORDER BY z.name ASC LIMIT 1`), and that zone is unrelated to the job and
 * usually cannot see it. The user lands somewhere they did not ask for and the
 * job appears not to exist. That was bug 6797.
 */

export interface JobZoneLike {
  id: string;
  type?: string | null;
  parent?: { id: string } | null;
}

export interface JobLike {
  zone?: JobZoneLike | null;
  sharedZones?: Array<string | null> | null;
}

/** A zone the user can context into, as returned by `availableZones`. */
export interface AvailableZoneLike {
  id: string;
  parent?: { id: string } | null;
}

/** Guard against a cycle in malformed zone data. Deeper than any real tree. */
const MAX_ZONE_DEPTH = 16;

/**
 * `zoneId` plus every ancestor of it that appears in `availableZones`.
 *
 * `availableZones` carries only a direct `parent` link per zone, so ancestry is
 * walked one hop at a time through that set — the same approach the backend
 * takes in `zone-cache.ts` (`findZoneInCache`).
 */
function zoneAndAncestors(
  zoneId: string,
  availableZones: readonly AvailableZoneLike[],
): Set<string> {
  const chain = new Set<string>([zoneId]);

  let currentId = availableZones.find((z) => z.id === zoneId)?.parent?.id;
  for (let depth = 0; currentId && depth < MAX_ZONE_DEPTH; depth++) {
    if (chain.has(currentId)) { break; }
    chain.add(currentId);
    currentId = availableZones.find((z) => z.id === currentId)?.parent?.id;
  }

  return chain;
}

/**
 * Can a user sitting in `zoneId` see this job?
 *
 * True when `zoneId` is the job's owning zone, an ancestor of it, one of the
 * job's shared zones, or an ancestor of one of those. The ancestor cases matter:
 * a user contexted into the `ymm-relentless` business group can see jobs shared
 * with the `ymm-tulsa` franchise beneath it, and must not be moved out of it.
 *
 * This mirrors the backend's job-visibility rule, which matches
 * `-[:IN_ZONE|SHARED_WITH_ZONE]->` against the zone and its descendants
 * (`neo4jSnippets.ts`, `zoneDir: 'lte'`).
 */
// Requirement: A job is visible from a zone when the zone is the job's
// owning zone, an ancestor of it, a shared zone, or an ancestor of a
// shared zone. This mirrors the backend's zoneDir: 'lte' visibility rule.
export function canSeeJobFromZone(
  job: JobLike | undefined | null,
  zoneId: string | undefined | null,
  availableZones: readonly AvailableZoneLike[] = [],
): boolean {
  if (!job || !zoneId) { return false; }

  const jobZoneIds = [
    job.zone?.id,
    ...(job.sharedZones ?? []),
  ].filter((id): id is string => Boolean(id));

  return jobZoneIds.some((jobZoneId) =>
    zoneAndAncestors(jobZoneId, availableZones).has(zoneId),
  );
}

/**
 * The zone to context into before working on `job`.
 *
 * Resolution order:
 *
 *   1. The current zone, if it can already see the job. Switching away from a
 *      working context is what broke 6797 — a successful fetch was thrown away
 *      by moving to the job's owning zone.
 *   2. A shared zone the user can actually context into. This is the case the
 *      old code missed entirely: it consulted only `job.zone`.
 *   3. The owning zone's parent when the owning zone is an `area`, else the
 *      owning zone itself — the original behaviour, kept as the fallback for
 *      jobs in the user's own franchise's areas, which is what it was written
 *      for.
 *
 * Returns `undefined` when there is no job zone to resolve, meaning "leave the
 * context alone" — callers must not switch on `undefined`.
 *
 * `availableZones` must be the *loaded* list. Pass `undefined`/`null` when it
 * has not loaded yet and the current zone is returned unchanged; an empty array
 * means "loaded, and the user can enter nothing", which is resolved normally.
 */
export function resolveJobContextZone(
  job: JobLike | undefined | null,
  currentZoneId: string | undefined | null,
  availableZones: readonly AvailableZoneLike[] | null | undefined,
): string | undefined {
  if (!job?.zone?.id) { return undefined; }

  // The zone list has not loaded yet. AvailableZonesService publishes it
  // asynchronously through valueChanges, so a cold deep link can reach here
  // before the first emission. Without it we cannot tell which zones the user
  // can enter or how they nest, so every branch below would be guessing — and
  // the area->parent fallback in particular would guess the job's *owning*
  // zone, which for a shared job is precisely the zone the user cannot enter.
  // Leave the context alone instead: if the current zone genuinely cannot see
  // the job, the fetch fails and the availableZones(objectId:) recovery path
  // resolves it with real data. Treating this as an empty list would silently
  // reintroduce the owning-zone fallback for users whose access comes from an
  // ancestor zone.
  if (!availableZones) { return currentZoneId ?? undefined; }

  // Requirement: When the job's owning franchise is contextable by the user,
  // context into it — even if the current zone can already see the job via
  // sharing or ancestry. The user must be in the owning franchise to edit the
  // job; staying in an ancestor or shared zone triggers "This job is shared.
  // It can only be edited from its original zone."
  const jobFranchiseId = job.zone.type === 'area' && job.zone.parent
    ? job.zone.parent.id
    : job.zone.id;

  if (jobFranchiseId && availableZones.some((z) => z.id === jobFranchiseId)) {
    return jobFranchiseId;
  }

  if (canSeeJobFromZone(job, currentZoneId, availableZones)) {
    return currentZoneId ?? undefined;
  }

  const contextableSharedZone = (job.sharedZones ?? [])
    .filter((id): id is string => Boolean(id))
    .find((id) => availableZones.some((zone) => zone.id === id));

  if (contextableSharedZone) { return contextableSharedZone; }

  const { zone } = job;
  return zone.type === 'area' && zone.parent ? zone.parent.id : zone.id;
}
